import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';
import { createBrowserRouter, Navigate } from 'react-router-dom';

// zercode packages
import { ProtectedRoute } from '@zercode/auth';
import { Loadable, LicenseUpsell, Loader } from '@zercode/ui';

// project imports
import { APP_CODE } from '../lib/appCode';
import { loginUrl } from '../lib/zercode';

// C-8: per-route license guard (permission is checked at the menu + backend; this adds the
// license dimension + a friendly upsell for direct-URL access to an unlicensed module).
const licensed = (moduleKey: string, element: React.ReactNode) => (
  <ProtectedRoute
    loginUrl={loginUrl}
    fallback={<Loader />}
    requiredModule={moduleKey}
    upsell={
      <LicenseUpsell
        requiredModule={moduleKey}
        title={<FormattedMessage id="license-upsell-title" defaultMessage="Bu özellik planınıza dahil değil" />}
        description={
          <FormattedMessage
            id="license-upsell-desc"
            defaultMessage="Mevcut planınız bu modülü içermiyor. Yükseltmek için yöneticinizle görüşün."
          />
        }
      />
    }
  >
    {element}
  </ProtectedRoute>
);

// Berry layouts (via seam alias)
import MainLayout from 'berry/layout/MainLayout';
import LandingRedirect from '../components/LandingRedirect';
import RouteError from '../components/RouteError';
import CrashTestPage from '../pages/CrashTest'; // ⚠ TEMPORARY

const DashboardPage = Loadable(lazy(() => import('../pages/dashboard/DashboardPage')));
const BrandingSettingsPage = Loadable(lazy(() => import('../pages/settings/Branding')));
const IntegrationsPage = Loadable(lazy(() => import('../pages/settings/Integrations'))); // C-6a
const NotificationDefaultsPage = Loadable(lazy(() => import('../pages/settings/NotificationDefaults'))); // C-6b
const LocaleDefaultsPage = Loadable(lazy(() => import('../pages/settings/LocaleDefaults'))); // C-16b
const LandingPagesSettingsPage = Loadable(lazy(() => import('../pages/settings/LandingPages'))); // LAND-CFG
// SITE-CMS D9: this tenant's OWN public pages. Same gate as branding (controlplane.branding.update +
// TenantScopedWriteAccess); the platform default is edited in super-admin instead.
const PublicSiteSettingsPage = Loadable(lazy(() => import('../pages/settings/PublicSite')));

// P1-3d — Account Center (Identity :5101; profil/güvenlik/oturumlar)
const ProfilePage = Loadable(lazy(() => import('../pages/account/Profile')));

// P2-1c — Certification (Certification :5900; şablon/verilen sertifika/doğrulama)
const CertTemplatesPage = Loadable(lazy(() => import('../pages/certification/Templates')));
const CertificatesPage = Loadable(lazy(() => import('../pages/certification/Certificates')));
const CertVerifyPage = Loadable(lazy(() => import('../pages/certification/Verify')));

// P2-2c — Growth (Growth :5910; kişiler/CRM/kampanyalar)
const GrowthContactsPage = Loadable(lazy(() => import('../pages/growth/Contacts')));
const GrowthCrmPage = Loadable(lazy(() => import('../pages/growth/Crm')));
// CRM C5: record pages (docs/crm-module-plan.md §7)
const CrmLeadPage = Loadable(lazy(() => import('../pages/growth/crm/LeadDetail')));
const CrmOpportunityPage = Loadable(lazy(() => import('../pages/growth/crm/OpportunityDetail')));
const CrmAccountPage = Loadable(lazy(() => import('../pages/growth/crm/AccountDetail')));
// EMAIL-MKT P2: Growth › Email Marketing (campaigns / audiences / settings), the campaign wizard and the report.
const GrowthEmailPage = Loadable(lazy(() => import('../pages/growth/email/EmailMarketing')));
const GrowthCampaignWizard = Loadable(lazy(() => import('../pages/growth/email/CampaignWizard')));
const GrowthCampaignReport = Loadable(lazy(() => import('../pages/growth/email/CampaignReport')));
const GrowthSocialPage = Loadable(lazy(() => import('../pages/growth/Social')));

// P3-1 — Commerce (Commerce :5920; ürün kataloğu/siparişler/checkout/iade)
const CommerceProductsPage = Loadable(lazy(() => import('../pages/commerce/Products')));
const SeasonSettingsPage = Loadable(lazy(() => import('../pages/commerce/SeasonSettings'))); // PCOM (mig 650)
const PartnerCommissionsPage = Loadable(lazy(() => import('../pages/commerce/PartnerCommissions'))); // PCOM P4 (mig 654)
const PartnerStatementPage = Loadable(lazy(() => import('../pages/commerce/PartnerStatement'))); // PCOM P4 (mig 654)
const PartnerCreditsPage = Loadable(lazy(() => import('../pages/commerce/PartnerCredits'))); // PCOM P6 (mig 657)
// PRD-GRP (Commerce mig 637) — one product group; the list is the Products page's "Product groups" tab.
const CommerceProductGroupFormPage = Loadable(lazy(() => import('../pages/commerce/ProductGroupFormPage')));
const CommerceQuotesPage = Loadable(lazy(() => import('../pages/commerce/quotes/Quotes')));
const CommerceQuoteDetailPage = Loadable(lazy(() => import('../pages/commerce/quotes/QuoteDetail')));
const GrowthSalesPerformancePage = Loadable(lazy(() => import('../pages/growth/SalesPerformance')));
const CommercePricingRulesPage = Loadable(lazy(() => import('../pages/commerce/PricingRules')));
const CommercePricingRuleFormPage = Loadable(lazy(() => import('../pages/commerce/PricingRuleFormPage')));
const CommerceCashRegistersPage = Loadable(lazy(() => import('../pages/commerce/CashRegisters'))); // CASH
const CommerceDiscountsPage = Loadable(lazy(() => import('../pages/commerce/Discounts'))); // DISC-CODE (migs 440/441/442)
// CASH-DETAIL: bir kasanın defteri (bakiye + hareketler).
const CommerceCashRegisterDetailPage = Loadable(lazy(() => import('../pages/commerce/CashRegisterDetail')));
// PAY-DOC: onay bekleyen dekontlar kuyruğu (commerce.installments.approve, mig 357).
const CommercePaymentApprovalsPage = Loadable(lazy(() => import('../pages/commerce/PaymentApprovals')));
const CommerceOrdersPage = Loadable(lazy(() => import('../pages/commerce/Orders')));
const CommerceOrderDetailPage = Loadable(lazy(() => import('../pages/commerce/OrderDetail'))); // FE-5
const CommerceOrderCreatePage = Loadable(lazy(() => import('../pages/commerce/OrderCreate'))); // ORD-NEW
// INST-MOD (mig 696): every instalment in the tenant, overdue first (commerce.installments.view).
const CommerceInstallmentsPage = Loadable(lazy(() => import('../pages/commerce/Installments')));
const CommercePurchasePage = Loadable(lazy(() => import('../pages/commerce/Purchase')));

// P3-2 — Billing (Billing :5930; faturalar/export/payout mutabakatı)
const DocumentIssuerProfilePage = Loadable(lazy(() => import('../pages/commerce/DocumentIssuerProfile'))); // DOC-ISS (mig 378)
const BillingInvoicesPage = Loadable(lazy(() => import('../pages/billing/Invoices')));
const BillingExportQueuePage = Loadable(lazy(() => import('../pages/billing/ExportQueue'))); // C-6c
const BillingPayoutsPage = Loadable(lazy(() => import('../pages/billing/Payouts')));

// P4-1 — HR (HR :5940; çalışan/izin/bordro/masraf)
const HrEmployeesPage = Loadable(lazy(() => import('../pages/hr/Employees')));
const HrLeavePage = Loadable(lazy(() => import('../pages/hr/Leave')));
const HrPayrollPage = Loadable(lazy(() => import('../pages/hr/Payroll')));
const HrExpensesPage = Loadable(lazy(() => import('../pages/hr/Expenses')));
const OpsTasksPage = Loadable(lazy(() => import('../pages/operations/tasks/Tasks')));
const OpsJobsPage = Loadable(lazy(() => import('../pages/operations/jobs/Jobs')));
const OpsJobTypesPage = Loadable(lazy(() => import('../pages/operations/jobs/JobTypes')));
const OpsJobTypeEditorPage = Loadable(lazy(() => import('../pages/operations/jobs/JobTypeEditor')));

// P4-2 — Operations (Operations :5950; dokümanlar + help desk)
const OpsDocumentsPage = Loadable(lazy(() => import('../pages/operations/Documents')));
const OpsTicketsPage = Loadable(lazy(() => import('../pages/operations/Tickets')));
// DES Alternatif B: talep detayı artık DIALOG değil, kendi ROTASI olan bir sayfa.
const OpsTicketDetailPage = Loadable(lazy(() => import('../pages/operations/TicketDetail')));

// C-2: the platform tenant-management console (Overview + Tenants) was retired here — the
// dedicated super-admin SPA is the single platform console. Tenant branding stays (settings).

// A3 — knowledge screens (Knowledge :5500)
const KbArticlesPage = Loadable(lazy(() => import('../pages/knowledge/Articles')));
const KbSearchPage = Loadable(lazy(() => import('../pages/knowledge/Search')));
const KbFaqPage = Loadable(lazy(() => import('../pages/knowledge/Faq')));

// A6 — AI screens (AiGateway :5300)
const AiPlaygroundPage = Loadable(lazy(() => import('../pages/ai/Playground')));
const AiPromptsPage = Loadable(lazy(() => import('../pages/ai/Prompts')));
const AiApprovalsPage = Loadable(lazy(() => import('../pages/ai/Approvals')));

// A4 — notification screens (Notification :5400)
const InboxPage = Loadable(lazy(() => import('../pages/notifications/Inbox')));
const NotifTemplatesPage = Loadable(lazy(() => import('../pages/notifications/Templates')));
const NotifTemplateManagePage = Loadable(lazy(() => import('../pages/notifications/TemplateManage')));
const NotifMailLayoutPage = Loadable(lazy(() => import('../pages/notifications/MailLayout')));
const NotifDiscussPage = Loadable(lazy(() => import('../pages/notifications/Discuss'))); // P4-4
const NotifPreferencesPage = Loadable(lazy(() => import('../pages/notifications/Preferences'))); // P5-NO

// S-23 — identity screens (Identity :5101; identity.users.read / identity.roles.read)
const UsersPage = Loadable(lazy(() => import('../pages/identity/Users')));
const UserDetailPage = Loadable(lazy(() => import('../pages/identity/UserDetail'))); // UF — user detail
const UserMergesPage = Loadable(lazy(() => import('../pages/identity/merge/UserMergesPage'))); // USER-MERGE — history
const UserReportsPage = Loadable(lazy(() => import('../pages/identity/userReport/UserReportsPage'))); // USER-REPORT — list
const UserReportPage = Loadable(lazy(() => import('../pages/identity/userReport/UserReportPage'))); // USER-REPORT — one person
const UserMergePage = Loadable(lazy(() => import('../pages/identity/merge/UserMergePage'))); // USER-MERGE — preview/progress
const SchoolMergesPage = Loadable(lazy(() => import('../pages/identity/merge/SchoolMergesPage'))); // SCHOOL-MERGE — history
const SchoolMergePage = Loadable(lazy(() => import('../pages/identity/merge/SchoolMergePage'))); // SCHOOL-MERGE — preview/progress
const RolesPage = Loadable(lazy(() => import('../pages/identity/Roles')));
const CompaniesPage = Loadable(lazy(() => import('../pages/identity/Companies'))); // CO
const CompanyDetailPage = Loadable(lazy(() => import('../pages/identity/CompanyDetail'))); // CV
const SchoolsPage = Loadable(lazy(() => import('../pages/identity/Schools'))); // SCH
const RoleRequestsPage = Loadable(lazy(() => import('../pages/identity/RoleRequests'))); // RSU
const SchoolDetailPage = Loadable(lazy(() => import('../pages/identity/SchoolDetail'))); // SCV

// DEF-1: tenant reference-data (definitions) admin module
const DefinitionGroupsPage = Loadable(lazy(() => import('../pages/definitions/Groups')));
const DefinitionGroupDetailPage = Loadable(lazy(() => import('../pages/definitions/GroupDetail')));
const AgeCategoriesPage = Loadable(lazy(() => import('../pages/definitions/AgeCategories')));
// INST-OPT: Commerce servisinde yaşar ama Tanımlar menü grubunda görünür (bkz. sayfa başlığı).
const InstallmentOptionsPage = Loadable(lazy(() => import('../pages/definitions/InstallmentOptions')));
// OFR-HIER: partner price-offer approval chains. Engagement-owned data, grouped under Definitions.
const OfferChainsPage = Loadable(lazy(() => import('../pages/definitions/OfferChains')));
// MEDAL: medal bands by percentile rank. Exam-owned data, grouped under Definitions.
const MedalCriteriaPage = Loadable(lazy(() => import('../pages/definitions/MedalCriteria')));
// OFR-APR: the approval queue. Engagement-owned data, grouped under the Commerce menu.
const OffersPage = Loadable(lazy(() => import('../pages/commerce/Offers')));

// S-20 — education screens (Learning :5600, Exam :5700, Engagement :5800)
const CoursesPage = Loadable(lazy(() => import('../pages/education/Courses')));
const CourseDetailPage = Loadable(lazy(() => import('../pages/education/CourseDetail')));
const EnrollmentsPage = Loadable(lazy(() => import('../pages/education/Enrollments')));
const ExamsPage = Loadable(lazy(() => import('../pages/education/Exams')));
const ExamArrangementsPage = Loadable(lazy(() => import('../pages/education/ExamArrangements'))); // EXTRA-TIME
const ExamDetailPage = Loadable(lazy(() => import('../pages/education/ExamDetail'))); // FE-4
const QuizzesPage = Loadable(lazy(() => import('../pages/education/Quizzes'))); // QUIZ (mig 586)
const QuizDetailPage = Loadable(lazy(() => import('../pages/education/QuizDetail')));
const LibraryPage = Loadable(lazy(() => import('../pages/education/Library'))); // LRN-LIB (mig 596)
const LibraryItemDetailPage = Loadable(lazy(() => import('../pages/education/LibraryItemDetail')));
const QuestionBanksPage = Loadable(lazy(() => import('../pages/education/QuestionBanks')));
const GradingPage = Loadable(lazy(() => import('../pages/education/Grading')));
const EventsPage = Loadable(lazy(() => import('../pages/education/Events')));
const EventFormPage = Loadable(lazy(() => import('../pages/education/EventFormPage'))); // EV — create/edit as a page
const AppointmentsPage = Loadable(lazy(() => import('../pages/education/Appointments')));
const AppointmentDetailPage = Loadable(lazy(() => import('../pages/education/AppointmentDetail'))); // APPT
const AttendancePage = Loadable(lazy(() => import('../pages/education/Attendance')));
// RPT (mig 265): per-event READ-ONLY reports. They replaced the counsellor visa-CASE page that
// used to live at education/visa (removed in RM-VISA, mig 276).
const PassportVisaReportPage = Loadable(lazy(() => import('../pages/education/PassportVisaReport')));
const TravelReportPage = Loadable(lazy(() => import('../pages/education/TravelReport')));
// EVR: the Event Report module — ONE tabbed page over every event at once, narrowed by a dropdown.
const EventReportsPage = Loadable(lazy(() => import('../pages/education/EventReports')));
// EXAM-REPORT: one row per student × exam, from the latest attempt.
const ExamReportPage = Loadable(lazy(() => import('../pages/education/ExamReport')));
// EXAM-PREVIEW: the author sits their own paper — OUTSIDE MainLayout, like the candidate runner.
const ExamPreviewPage = Loadable(lazy(() => import('../pages/education/ExamPreview')));
const CoursePreviewPage = Loadable(lazy(() => import('../pages/education/CoursePreview')));

// ==============================|| ROUTING ||============================== //

// One pathless parent so EVERY route — including ProtectedRoute and MainLayout themselves — renders
// RouteError on a crash, instead of react-router's built-in developer screen.
const router = createBrowserRouter(
  [
    {
      errorElement: <RouteError />,
      children: [
        {
          path: '/',
          element: (
            <ProtectedRoute loginUrl={loginUrl} fallback={<Loader />} requiredApp={APP_CODE}>
              <MainLayout />
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <LandingRedirect /> },
            { path: 'dashboard', element: <DashboardPage /> },
            // ⚠ TEMPORARY crash test for RouteError — NOT STAGED, remove before committing.
            { path: 'crash-test', element: <CrashTestPage /> },
            { path: 'dashboard/:tab', element: <DashboardPage /> },
            { path: 'education/courses', element: <CoursesPage /> },
            { path: 'education/courses/:id', element: <CourseDetailPage /> },
            { path: 'education/enrollments', element: <EnrollmentsPage /> },
            { path: 'education/exams', element: <ExamsPage /> },
            { path: 'education/exam-arrangements', element: <ExamArrangementsPage /> },
            { path: 'education/exams/:id', element: <ExamDetailPage /> },
            { path: 'education/quizzes', element: <QuizzesPage /> },
            { path: 'education/quizzes/:id', element: <QuizDetailPage /> },
            { path: 'education/library', element: <LibraryPage /> },
            { path: 'education/library/:id', element: <LibraryItemDetailPage /> },
            { path: 'education/question-banks', element: <QuestionBanksPage /> },
            { path: 'education/grading', element: <GradingPage /> },
            { path: 'education/events', element: <EventsPage /> },
            { path: 'education/events/new', element: <EventFormPage /> },
            { path: 'education/events/:id/edit', element: <EventFormPage /> },
            { path: 'education/appointments', element: <AppointmentsPage /> },
            { path: 'education/appointments/:id', element: <AppointmentDetailPage /> },
            { path: 'education/attendance', element: <AttendancePage /> },
            { path: 'education/passport-visa-report', element: <PassportVisaReportPage /> },
            { path: 'education/travel-report', element: <TravelReportPage /> },
            { path: 'education/event-report', element: <EventReportsPage /> },
            // EVR-ALL: the SAME page. `:id` used to be the drill-down's destination and is still where every
            // bookmark and old link points, so it opens the report with that event already chosen rather than
            // 404-ing on a path the product used to have.
            { path: 'education/event-report/:id', element: <EventReportsPage /> },
            { path: 'education/exam-report', element: <ExamReportPage /> },
            { path: 'ai/playground', element: licensed('ai_assist', <AiPlaygroundPage />) },
            { path: 'ai/prompts', element: licensed('ai_assist', <AiPromptsPage />) },
            { path: 'ai/approvals', element: licensed('ai_assist', <AiApprovalsPage />) },
            { path: 'notifications/inbox', element: <InboxPage /> },
            { path: 'notifications/templates', element: <NotifTemplatesPage /> },
            // MAIL-LAYOUT: the shared shell. ⚠ NOT under notifications/templates/ — that path is the
            // :key route below, and a layout is not a template named "layout".
            { path: 'notifications/mail-layout', element: <NotifMailLayoutPage /> },
            // TPL-MANAGE: a key's versions. The key is [a-z0-9_.] only, so it is safe as a bare segment.
            { path: 'notifications/templates/:key', element: <NotifTemplateManagePage /> },
            { path: 'notifications/discuss', element: <NotifDiscussPage /> },
            { path: 'notifications/preferences', element: <NotifPreferencesPage /> },
            { path: 'knowledge/articles', element: <KbArticlesPage /> },
            { path: 'knowledge/faq', element: <KbFaqPage /> },
            { path: 'knowledge/search', element: <KbSearchPage /> },
            { path: 'identity/users', element: <UsersPage /> },
            { path: 'identity/users/merges', element: <UserMergesPage /> },
            { path: 'identity/users/merges/:id', element: <UserMergePage /> },
            { path: 'identity/schools/merges', element: <SchoolMergesPage /> },
            { path: 'identity/schools/merges/:id', element: <SchoolMergePage /> },
            { path: 'identity/users/:id', element: <UserDetailPage /> },
            { path: 'identity/user-reports', element: <UserReportsPage /> },
            { path: 'identity/user-reports/:id', element: <UserReportPage /> },
            { path: 'identity/roles', element: <RolesPage /> },
            { path: 'identity/companies', element: <CompaniesPage /> },
            { path: 'identity/companies/:id', element: <CompanyDetailPage /> },
            { path: 'identity/role-requests', element: <RoleRequestsPage /> },
            { path: 'identity/schools', element: <SchoolsPage /> },
            { path: 'identity/schools/:id', element: <SchoolDetailPage /> },
            { path: 'definitions/age-categories', element: <AgeCategoriesPage /> },
            { path: 'definitions/installment-options', element: <InstallmentOptionsPage /> },
            { path: 'definitions/offer-chains', element: <OfferChainsPage /> },
            { path: 'definitions/medal-criteria', element: <MedalCriteriaPage /> },
            { path: 'commerce/offers', element: <OffersPage /> },
            { path: 'commerce/season', element: <SeasonSettingsPage /> },
            { path: 'commerce/partner-commissions', element: <PartnerCommissionsPage /> },
            { path: 'commerce/partner-commissions/:companyId', element: <PartnerStatementPage /> },
            { path: 'commerce/partner-credits', element: <PartnerCreditsPage /> },
            { path: 'definitions/groups', element: <DefinitionGroupsPage /> },
            { path: 'definitions/groups/:id', element: <DefinitionGroupDetailPage /> },
            { path: 'settings/branding', element: <BrandingSettingsPage /> },
            { path: 'settings/integrations', element: <IntegrationsPage /> },
            { path: 'settings/notifications', element: <NotificationDefaultsPage /> },
            { path: 'settings/locale', element: <LocaleDefaultsPage /> },
            { path: 'settings/landing', element: <LandingPagesSettingsPage /> },
            { path: 'settings/site', element: <PublicSiteSettingsPage /> },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'certification/templates', element: <CertTemplatesPage /> },
            { path: 'certification/certificates', element: <CertificatesPage /> },
            { path: 'certification/verify', element: <CertVerifyPage /> },
            { path: 'growth/contacts', element: <GrowthContactsPage /> },
            { path: 'growth/crm', element: <GrowthCrmPage /> },
            { path: 'growth/crm/leads/:id', element: <CrmLeadPage /> },
            { path: 'growth/crm/opportunities/:id', element: <CrmOpportunityPage /> },
            { path: 'growth/crm/accounts/:id', element: <CrmAccountPage /> },
            { path: 'growth/sales-performance', element: <GrowthSalesPerformancePage /> },
            { path: 'growth/sales-performance/:salespersonId', element: <GrowthSalesPerformancePage /> },
            { path: 'growth/campaigns', element: <GrowthEmailPage /> },
            { path: 'growth/campaigns/new', element: <GrowthCampaignWizard /> },
            { path: 'growth/campaigns/:id/edit', element: <GrowthCampaignWizard /> },
            { path: 'growth/campaigns/:id', element: <GrowthCampaignReport /> },
            { path: 'growth/social', element: <GrowthSocialPage /> },
            { path: 'commerce/pricing-rulesets', element: <CommercePricingRulesPage /> },
            { path: 'commerce/pricing-rulesets/new', element: <CommercePricingRuleFormPage /> },
            { path: 'commerce/pricing-rulesets/:id/edit', element: <CommercePricingRuleFormPage /> },
            { path: 'commerce/products', element: <CommerceProductsPage /> },
            { path: 'commerce/product-groups/new', element: <CommerceProductGroupFormPage /> },
            // SALES (Commerce mig 743): quotes on CRM opportunities; the menu entry is Growth › Quotes.
            { path: 'commerce/quotes', element: <CommerceQuotesPage /> },
            { path: 'commerce/quotes/:id', element: <CommerceQuoteDetailPage /> },
            { path: 'commerce/product-groups/:id/edit', element: <CommerceProductGroupFormPage /> },
            { path: 'commerce/orders', element: <CommerceOrdersPage /> },
            { path: 'commerce/orders/new', element: <CommerceOrderCreatePage /> },
            // ⚠ AFTER '/new' AND THAT IS NOT COSMETIC: react-router ranks a static segment above a dynamic
            // one, but keeping them adjacent in this order is what stops a future edit from turning 'new'
            // into an order id the detail page would fetch and 404 on.
            { path: 'commerce/orders/:id', element: <CommerceOrderDetailPage /> },
            { path: 'commerce/installments', element: <CommerceInstallmentsPage /> },
            { path: 'commerce/purchase', element: <CommercePurchasePage /> },
            { path: 'commerce/cash-registers', element: <CommerceCashRegistersPage /> },
            { path: 'commerce/cash-registers/:id', element: <CommerceCashRegisterDetailPage /> },
            { path: 'commerce/discounts', element: <CommerceDiscountsPage /> },
            { path: 'commerce/payment-approvals', element: <CommercePaymentApprovalsPage /> },
            // DOC-ISS2: the page now lives under Settings. The OLD path stays as a redirect rather than
            // a 404 — it was linked from the Commerce menu for weeks and is certainly bookmarked.
            { path: 'commerce/document-issuer-profile', element: <Navigate to="/settings/document-issuer" replace /> },
            { path: 'settings/document-issuer', element: <DocumentIssuerProfilePage /> },
            { path: 'billing/invoices', element: <BillingInvoicesPage /> },
            { path: 'billing/export', element: <BillingExportQueuePage /> },
            { path: 'billing/payouts', element: <BillingPayoutsPage /> },
            { path: 'hr/employees', element: <HrEmployeesPage /> },
            { path: 'hr/leave', element: <HrLeavePage /> },
            { path: 'hr/payroll', element: <HrPayrollPage /> },
            { path: 'hr/expenses', element: <HrExpensesPage /> },
            { path: 'operations/documents', element: <OpsDocumentsPage /> },
            { path: 'operations/tickets', element: <OpsTicketsPage /> },
            { path: 'operations/tickets/:id', element: <OpsTicketDetailPage /> },
            { path: 'operations/tasks', element: <OpsTasksPage /> },
            { path: 'operations/jobs', element: <OpsJobsPage /> },
            { path: 'operations/job-types', element: <OpsJobTypesPage /> },
            { path: 'operations/job-types/:id', element: <OpsJobTypeEditorPage /> }
          ]
        },
        // EXAM-PREVIEW: a paper sat beside the admin sidebar is not the paper a candidate sits.
        {
          path: '/education/exams/:id/preview',
          element: (
            <ProtectedRoute loginUrl={loginUrl} fallback={<Loader />} requiredApp={APP_CODE}>
              <ExamPreviewPage />
            </ProtectedRoute>
          )
        },
        // LRN-LIB B6.8: a course seen as a learner sees it — full page, like the exam preview; nothing is recorded.
        {
          path: '/education/courses/:id/preview',
          element: (
            <ProtectedRoute loginUrl={loginUrl} fallback={<Loader />} requiredApp={APP_CODE}>
              <CoursePreviewPage />
            </ProtectedRoute>
          )
        },
        { path: '*', element: <LandingRedirect /> }
      ]
    }
  ],
  { basename: import.meta.env.VITE_APP_BASE_NAME || '/' }
);

export default router;
